import React from "react";
import LegalLayout, { Section, P, UL, LI, Callout } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      code="DOC · 02 / PRIVACY POLICY"
      title="Privacy Policy"
      lastUpdated="December 2025"
    >
      <Section id="intro" title="1. Introduction">
        <P>
          <strong>Sebyx</strong> ("Sebyx", "we", "us") is committed to
          protecting the privacy of every visitor, lead, and paying customer of{" "}
          <strong>www.sebyx.com</strong> (the "Site"). This Privacy Policy
          explains what personal data we collect, how we use it, with whom we
          share it, and what rights you have under applicable Indian data
          protection laws — including the Information Technology Act, 2000, the
          IT (Reasonable Security Practices) Rules, 2011, and the Digital
          Personal Data Protection Act, 2023 ("DPDP Act").
        </P>
      </Section>

      <Section id="data-collected" title="2. Information We Collect">
        <P>We collect the following categories of personal data:</P>
        <UL>
          <LI>
            <strong>Contact data:</strong> name, work email, company, phone
            number, and message content — submitted via the Strategy Audit form
            or any of our enrollment flows.
          </LI>
          <LI>
            <strong>Payment data:</strong> we do <strong>not</strong> store full
            card or UPI credentials. Razorpay processes the payment and shares
            only the order ID, payment ID, signature, status, and the masked
            method (e.g., "card · last4 4242") with us.
          </LI>
          <LI>
            <strong>Engagement data:</strong> tier purchased, cohort
            attendance, mentorship session notes, deliverables received.
          </LI>
          <LI>
            <strong>Technical data:</strong> IP address, device, browser, and
            anonymous analytics events from tools such as PostHog or Google
            Analytics (if enabled).
          </LI>
        </UL>
      </Section>

      <Section id="how-we-use" title="3. How We Use Your Data">
        <UL>
          <LI>To deliver the services you've enrolled in or requested.</LI>
          <LI>
            To send transactional notifications (enrollment receipts, cohort
            schedule, mentorship reminders).
          </LI>
          <LI>
            To respond to Strategy Audit, BDaaS, or partnership inquiries.
          </LI>
          <LI>
            To improve our curriculum, proprietary tech, and Site experience —
            using aggregated, de-identified analytics.
          </LI>
          <LI>
            To comply with tax, accounting, and legal obligations in India.
          </LI>
        </UL>
        <Callout>
          We do <strong>not</strong> sell, rent, or trade your personal data
          to third parties for advertising.
        </Callout>
      </Section>

      <Section id="sharing" title="4. Who We Share Data With">
        <P>
          We share strictly the minimum data required to deliver our services
          with the following processors:
        </P>
        <UL>
          <LI>
            <strong>Razorpay Software Private Limited</strong> — payment
            processing, refunds, settlement.
          </LI>
          <LI>
            <strong>MongoDB Atlas (cloud hosting)</strong> — primary database
            of leads and enrollments, hosted in an Indian or APAC region.
          </LI>
          <LI>
            <strong>Email infrastructure provider</strong> (Zoho Mail / Resend
            or equivalent) — for transactional and inbox communication.
          </LI>
          <LI>
            <strong>Hosting provider (Emergent)</strong> — application hosting
            and SSL.
          </LI>
          <LI>
            <strong>Analytics providers (PostHog / Google Analytics, if
            enabled)</strong> — anonymous usage metrics.
          </LI>
          <LI>
            <strong>Statutory authorities</strong> — only when legally
            compelled by court order, subpoena, or applicable Indian law.
          </LI>
        </UL>
      </Section>

      <Section id="retention" title="5. Data Retention">
        <UL>
          <LI>
            Lead records are retained for up to 36 months from last contact
            unless you request earlier deletion.
          </LI>
          <LI>
            Enrollment and payment records are retained for at least 8 years to
            meet Indian tax and audit requirements.
          </LI>
          <LI>
            Anonymous analytics events are retained per the analytics
            provider's default policy (typically 14–26 months).
          </LI>
        </UL>
      </Section>

      <Section id="rights" title="6. Your Rights">
        <P>Subject to verification of identity, you have the right to:</P>
        <UL>
          <LI>Access the personal data we hold about you.</LI>
          <LI>Correct inaccurate or outdated data.</LI>
          <LI>
            Erase your personal data, except records we are required to retain
            by law.
          </LI>
          <LI>Withdraw consent for marketing communications at any time.</LI>
          <LI>Lodge a complaint with the Data Protection Board of India.</LI>
        </UL>
        <P>
          To exercise any right, write to{" "}
          <a
            href="mailto:contact@sebyx.com"
            className="text-sebyx-accent hover:underline"
          >
            contact@sebyx.com
          </a>{" "}
          with the subject line "Privacy Request". We respond within 30 days.
        </P>
      </Section>

      <Section id="security" title="7. Security">
        <P>
          We use TLS encryption in transit, encrypted storage at rest, and
          strict access controls. Despite reasonable safeguards, no system is
          impenetrable; you acknowledge that you share data at your own risk
          and agree to notify us immediately of any suspected compromise.
        </P>
      </Section>

      <Section id="cookies" title="8. Cookies">
        <P>
          The Site uses essential cookies for session management, plus
          analytics cookies (only when analytics is enabled). You can disable
          non-essential cookies via your browser settings without affecting
          core Site functionality.
        </P>
      </Section>

      <Section id="children" title="9. Children's Data">
        <P>
          Sebyx does not knowingly collect personal data from individuals under
          the age of 18. If we become aware of such collection, we will delete
          the data promptly.
        </P>
      </Section>

      <Section id="changes" title="10. Changes to This Policy">
        <P>
          We may update this Privacy Policy periodically. The "Last updated"
          date at the top of this page reflects the latest revision. Material
          changes will be highlighted on the Site for at least 14 days.
        </P>
      </Section>

      <Section id="contact" title="11. Grievance Officer">
        <P>
          In compliance with the IT Rules, 2011 and DPDP Act, 2023, the
          Grievance Officer for Sebyx can be reached at:
        </P>
        <Callout>
          <strong>Mandar Prabhudesai · Founder, Sebyx</strong>
          <br />
          Email:{" "}
          <a
            href="mailto:contact@sebyx.com"
            className="text-sebyx-accent hover:underline"
          >
            contact@sebyx.com
          </a>
          <br />
          Phone:{" "}
          <a
            href="tel:+919108436667"
            className="text-sebyx-accent hover:underline"
          >
            +91 91084 36667
          </a>
        </Callout>
      </Section>
    </LegalLayout>
  );
}
